import React, { memo, startTransition, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { getNoVehicleImageToDisplay } from 'site-modules/shared/utils/get-no-vehicle-image-to-display';
import { maybe } from 'site-modules/shared/utils/incentives/gql-incentives-filter';
import { formatPriceStringV2, getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { getLeaseDealsValueRating } from 'site-modules/lease-deals/utils/get-lease-deals-value-rating';
import { SOURCES } from 'client/utils/image-helpers';
import { Image } from 'client/site-modules/shared/components/image/image';
import { CompactRatingBar } from 'site-modules/lease-deals/components/lease-deals-compact-card/compact-rating-bar';
import { LeaseDealsDetailsModal } from 'site-modules/lease-deals/components/lease-deals-details-modal/lease-deals-details-modal';

import './lease-deals-compact-card.scss';

const getVehicleDetails = ({ valueRating, term, formattedDueAtSigning }) => {
  return [
    { label: 'Term', value: `${term} months` },
    { label: 'Due at signing', value: formattedDueAtSigning },
    maybe(valueRating)
      .map(value => ({
        label: 'Value',
        value: <CompactRatingBar value={value} />,
      }))
      .unpack(),
  ];
};

export const LeaseDealsCompactCard = memo(
  ({
    photo = undefined,
    bodyType = undefined,
    payment,
    make,
    model,
    year,
    effectivePaymentRatio,
    effectivePayment,
    dueAtSigning,
    term,
    styleId,
    isGemDeal = false,
    isShop4931ZeroDownRoute = false,
    ...restProps
  }) => {
    const [isOpenModal, setIsOpenModal] = useState(false);

    const toggleModal = useCallback(() => {
      startTransition(() => setIsOpenModal(prev => !prev));
    }, []);

    const displayedPayment = isShop4931ZeroDownRoute ? Math.round(effectivePayment) : payment;
    const displayedDueAtSigning = isShop4931ZeroDownRoute ? effectivePayment : dueAtSigning;
    const formattedDueAtSigning = getPriceStringV2(displayedDueAtSigning, 'Contact dealer', { allowZero: true });

    const errorImage = getNoVehicleImageToDisplay(bodyType);
    const valueRating = getLeaseDealsValueRating(effectivePaymentRatio);

    return (
      <>
        <div
          data-tracking-parent="edm-lease-deals-vehicle-card-compact"
          className="lease-deals-compact-card pos-r d-flex bg-white ps-0_5 py-0_5 pe-0_75 pe-md-1"
        >
          <Image
            lazy
            alt={photo?.title}
            source={SOURCES.MEDIA}
            errorImage={errorImage}
            imageId={photo?.href ?? ''}
            className="vehicle-image me-0_75 me-md-1_25"
          />

          <div className="d-flex align-items-center justify-content-between flex-grow-1 gap-0_75 gap-md-2">
            <div className="card-title d-flex flex-column">
              <h3 className="size-16 text-cool-gray-10 fw-medium text-start">{`${year} ${make} ${model}`}</h3>

              <span className="d-flex size-14 gap-0_25 hidden-md-up">
                <span>
                  <span className="visually-hidden">Term </span>
                  {term} mo
                </span>
                <span className="vr text-cool-gray-70 size-14 align-self-center" />
                <span>{formattedDueAtSigning} at signing</span>
              </span>
            </div>

            <ul className="details-list list-unstyled d-flex justify-content-between gap-2_5 hidden-md-down">
              {getVehicleDetails({ valueRating, term, formattedDueAtSigning })
                .filter(vehicle => vehicle)
                .map(({ label, value }) => (
                  <li key={label} className="d-flex flex-column gap-0_5 justify-content-around align-items-start">
                    <span className="size-14 we-medium text-cool-gray-40">{label}</span>
                    <span className="size-16 text-cool-gray-40">{value}</span>
                  </li>
                ))}
            </ul>

            <span className="monthly-payment-wrapper d-flex flex-column align-items-end">
              <span className="monthly-payment d-flex align-items-center">
                {isGemDeal && (
                  <Image
                    lazy
                    ariaHidden
                    source={SOURCES.STATIC}
                    imageId="/icons/lease-deals-gem.svg"
                    className="me-0_25"
                  />
                )}
                <span className="size-30 text-cool-gray-10 fw-bold">{formatPriceStringV2(displayedPayment)}</span>
                <small className="size-14 text-cool-gray-40 fw-medium">/mo*</small>
              </span>
              {valueRating && <CompactRatingBar value={valueRating} className="hidden-md-up" />}
            </span>
          </div>

          <button
            onClick={() => {
              toggleModal();
            }}
            data-tracking-value={styleId}
            data-tracking-id="view_full_description"
            className="pos-a top-0 left-0 w-100 h-100 border-0 bg-transparent"
            aria-label={`See more details on ${year} ${make} ${model} ${formatPriceStringV2(displayedPayment)}/mo deal`}
          />
        </div>

        <LeaseDealsDetailsModal
          {...restProps}
          photo={photo}
          bodyType={bodyType}
          payment={payment}
          make={make}
          model={model}
          year={year}
          effectivePayment={effectivePayment}
          dueAtSigning={dueAtSigning}
          term={term}
          styleId={styleId}
          isOpen={isOpenModal}
          onClose={toggleModal}
          isGemDeal={isGemDeal}
          effectivePaymentRatio={effectivePaymentRatio}
          isShop4931ZeroDownRoute={isShop4931ZeroDownRoute}
        />
      </>
    );
  }
);

LeaseDealsCompactCard.displayName = 'LeaseDealsCompactCard';

LeaseDealsCompactCard.propTypes = {
  photo: PropTypes.shape({ title: PropTypes.string, href: PropTypes.string }),
  bodyType: PropTypes.string,
  payment: PropTypes.number.isRequired,
  make: PropTypes.string.isRequired,
  model: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
  effectivePaymentRatio: PropTypes.number.isRequired,
  effectivePayment: PropTypes.number.isRequired,
  dueAtSigning: PropTypes.number.isRequired,
  term: PropTypes.number.isRequired,
  styleId: PropTypes.number.isRequired,
  isGemDeal: PropTypes.bool,
  isShop4931ZeroDownRoute: PropTypes.bool,
};
